import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI, AUTH_TOKEN_KEY } from '../lib/api';

const AuthContext = createContext(null);

// Session storage for token (cleared on browser close, more secure than localStorage)
// Note: For production, consider httpOnly cookies set by backend

// Get token from sessionStorage (memory-based session)
const getToken = () => sessionStorage.getItem(AUTH_TOKEN_KEY);
const setToken = (token) => sessionStorage.setItem(AUTH_TOKEN_KEY, token);
const removeToken = () => sessionStorage.removeItem(AUTH_TOKEN_KEY);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const userRef = useRef(null); // Keep user in memory, not storage

  const logout = useCallback(() => {
    removeToken();
    setUser(null);
    userRef.current = null;
  }, []);

  useEffect(() => {
    const token = getToken();
    
    if (token) {
      // Verify token is still valid and fetch user data
      authAPI.getMe()
        .then(res => {
          setUser(res.data);
          userRef.current = res.data;
        })
        .catch(() => {
          logout();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [logout]);

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    const { access_token, user: userData } = res.data;
    setToken(access_token);
    setUser(userData);
    userRef.current = userData;
    return userData;
  };

  const register = async (data) => {
    const res = await authAPI.register(data);
    const { access_token, user: userData } = res.data;
    setToken(access_token);
    setUser(userData);
    userRef.current = userData;
    return userData;
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
